import { createContext, use, useEffect } from 'react'

import { SITE_NAME, SITE_URL } from './config'

export interface HeadData {
  title: string
  description?: string
  /** Site-relative path used for the canonical URL (defaults to current). */
  path?: string
  jsonLd?: Record<string, unknown>
  noIndex?: boolean
}

/**
 * Minimal head manager. During prerendering the collector captures the head
 * for the route; in the browser an effect updates document metadata on
 * navigation. Both write the same tags so static and live pages agree.
 */
export interface HeadCollector {
  current: HeadData | null
}

export const HeadContext = createContext<HeadCollector | null>(null)

export function renderHeadTags(data: HeadData, path: string) {
  const title = escapeHtml(data.title)
  const description = escapeHtml(data.description ?? '')
  const canonical = `${SITE_URL}${path}`
  const tags = [
    `<title>${title}</title>`,
    `<meta name="description" content="${description}" />`,
    `<link rel="canonical" href="${canonical}" />`,
    `<meta property="og:type" content="article" />`,
    `<meta property="og:site_name" content="${escapeHtml(SITE_NAME)}" />`,
    `<meta property="og:title" content="${title}" />`,
    `<meta property="og:description" content="${description}" />`,
    `<meta property="og:url" content="${canonical}" />`,
    `<meta name="twitter:card" content="summary" />`,
  ]
  if (data.noIndex) tags.push(`<meta name="robots" content="noindex" />`)
  if (data.jsonLd) {
    tags.push(
      `<script type="application/ld+json">${JSON.stringify(data.jsonLd).replace(/</g, '\\u003c')}</script>`,
    )
  }
  return tags.join('\n    ')
}

export function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function setMeta(
  selector: string,
  attr: 'name' | 'property',
  key: string,
  content: string,
) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attr, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

export function useHead(data: HeadData) {
  const collector = use(HeadContext)
  if (collector) collector.current = data

  useEffect(() => {
    const path = data.path ?? window.location.pathname
    document.title = data.title
    const description = data.description ?? ''
    setMeta('meta[name="description"]', 'name', 'description', description)
    setMeta('meta[property="og:title"]', 'property', 'og:title', data.title)
    setMeta(
      'meta[property="og:description"]',
      'property',
      'og:description',
      description,
    )
    setMeta(
      'meta[property="og:url"]',
      'property',
      'og:url',
      `${SITE_URL}${path}`,
    )
    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    )
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.appendChild(canonical)
    }
    canonical.href = `${SITE_URL}${path}`
    const robots = document.head.querySelector('meta[name="robots"]')
    if (data.noIndex) {
      setMeta('meta[name="robots"]', 'name', 'robots', 'noindex')
    } else if (robots) {
      robots.remove()
    }
  }, [data.title, data.description, data.path, data.noIndex])
}
